/* @flow */

import * as React from 'react';
import { Link } from 'react-router-dom';
import type { TFunction, TranslatorProps } from 'react-i18next';

//----------------------------------------------------------------------//
// MATERIAL UI                                                          //
//----------------------------------------------------------------------//

import Button from '@material-ui/core/Button';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

import RequireForAccess from 'react-access';

import { createInviteUser, createNewLocation } from 'permissions/account';
import { createSingleOwnedAsset } from 'permissions/asset';
import { allRoles } from 'permissions/general';
import { createNewCostCenter } from 'permissions/costCenter';
import { formulateUri } from 'tables/components/ReportView/state';

export default class AssignedToTradingAccount extends React.Component<*> {
  renderDisabledButton = (item: Object) => {
    const t: TFunction = this.props.t;
    return (
      <Button color="primary" variant="contained" className="layout-button" disabled={true}>
        {t(`general:actions.view_${item.label}`, {
          count: item.count,
        })}
      </Button>
    );
  };

  renderCreateNewButton = (item: Object) => {
    const t: TFunction = this.props.t;
    return (
      <RequireForAccess permissions={item.permissions}>
        <Link to={item.newLink} className="layout-button">
          <Button color="primary" variant="contained">
            {t('general:actions.create_new')}
          </Button>
        </Link>
      </RequireForAccess>
    );
  };

  renderViewAllButton = (item: Object) => {
    const t: TFunction = this.props.t;
    return (
      <Link to={item.viewLink} className="layout-button">
        <Button color="primary" variant="contained">
          {t(`general:actions.view_${item.label}`, {
            count: item.count,
          })}
        </Button>
      </Link>
    );
  };

  render() {
    const t = this.props.t;

    const assignedToTradingAccount = [
      {
        id: 1,
        label: 'contract_open',
        count: this.props.accountStore.details.details.open_contracts_count,
        newLink: '',
        viewLink: formulateUri(`/orders/`, {
          page: 1,
          filters: [
            {
              key: 'er_status',
              values: ['open'],
            },
            {
              key: 'account_id',
              values: [this.props.details.details.id],
            },
          ],
        }),
        renderViewAll: this.renderViewAllButton,
        renderNew: this.renderDisabledButton,
        permissions: allRoles,
      },
      {
        id: 1,
        label: 'contract_closed',
        count: this.props.accountStore.details.details.closed_contracts_count,
        newLink: '',
        viewLink: formulateUri(`/orders/`, {
          page: 1,
          filters: [
            {
              key: 'er_status',
              values: ['closed'],
              overrideImplicit: true,
            },
            {
              key: 'account_id',
              values: [this.props.details.details.id],
            },
          ],
        }),
        renderViewAll: this.renderViewAllButton,
        renderNew: this.renderDisabledButton,
        permissions: allRoles,
      },
      {
        id: 2,
        label: 'asset_rented',
        count: this.props.accountStore.details.details.rental_assets_count,
        newLink: '/assets/create',
        viewLink: {
          pathname: `/assets/account/${this.props.details.details.id}/asset-type/Rented Open`,
        },
        renderViewAll: this.renderViewAllButton,
        renderNew: this.renderDisabledButton,
        permissions: createSingleOwnedAsset,
      },
      {
        id: 3,
        label: 'asset_sale',
        count: this.props.accountStore.details.details.sales_assets_count,
        newLink: '/assets/create',
        viewLink: formulateUri(`/assets/`, {
          page: 1,
          filters: [
            {
              key: 'asset_status',
              values: ['Sold'],
            },
            {
              key: 'account_id',
              values: [this.props.details.details.id],
            },
          ],
        }),
        renderViewAll: this.renderViewAllButton,
        renderNew: this.renderDisabledButton,
        permissions: createSingleOwnedAsset,
      },
      {
        id: 4,
        label: 'asset_owned',
        count: this.props.accountStore.details.details.owned_assets_count,
        newLink: '/assets/create',
        viewLink: formulateUri(`/assets/`, {
          page: 1,
          filters: [
            {
              key: 'asset_type',
              values: ['Owned'],
            },
            {
              key: 'account_id',
              values: [this.props.details.details.id],
            },
          ],
        }),
        renderViewAll: this.renderViewAllButton,
        renderNew: this.renderCreateNewButton,
        permissions: createSingleOwnedAsset,
      },
      {
        id: 5,
        label: 'project',
        count: this.props.accountStore.details.details.projects_count,
        newLink: '/projects/create',
        viewLink: formulateUri(`/projects/`, {
          page: 1,
          filters: [
            {
              key: 'account',
              values: [this.props.details.details.id],
            },
          ],
        }),
        renderViewAll: this.renderViewAllButton,
        renderNew: this.renderCreateNewButton,
        permissions: allRoles,
      },
      {
        id: 6,
        label: 'cost_center',
        count: this.props.accountStore.details.details.cost_centres_count,
        newLink: `/cost-centers/create`,
        viewLink: {
          pathname: `/cost-centers/account/${this.props.details.details.id}`,
        },
        renderViewAll: this.renderViewAllButton,
        renderNew: this.renderCreateNewButton,
        permissions: createNewCostCenter,
      },
      {
        id: 7,
        label: 'location',
        count: this.props.accountStore.details.details.locations_count,
        newLink: {
          pathname: `/accounts/${this.props.details.details.id}`,
          state: { tab: 2 },
          search: 'tab=2',
        },
        viewLink: {
          pathname: `/accounts/${this.props.details.details.id}`,
          state: { tab: 2 },
          search: 'tab=2',
        },
        renderViewAll: this.renderViewAllButton,
        renderNew: this.renderCreateNewButton,
        permissions: createNewLocation,
      },
      {
        id: 8,
        label: 'user',
        count: this.props.accountStore.details.details.users_count,
        newLink: `/accounts/${this.props.details.details.id}/user/invite`,
        viewLink: {
          pathname: `/accounts/${this.props.details.details.id}`,
          state: { tab: 1 },
          search: 'tab=1',
        },
        renderViewAll: this.renderViewAllButton,
        renderNew: this.renderCreateNewButton,
        permissions: createInviteUser,
      },
    ];

    return (
      <React.Fragment>
        <h3>{t('general:trading_accounts.assigned_to')}</h3>
        <table className="layout-table large-first-col">
          <tbody>
            {assignedToTradingAccount.map((item, index) => (
              <tr key={`${item.id}-${index}`}>
                <td>{t(`general:${item.label}_plural`)}</td>
                <td className="layout-flex">
                  <div>
                    <span>{item.count}</span>

                    {// eslint-disable-next-line
                    this.props.details.details.id !== undefined && item.count !== 0
                      ? item.renderViewAll(item)
                      : item.renderNew(item)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </React.Fragment>
    );
  }
}
